/**
 * MarkdownField — a lightweight markdown editor for modal text fields.
 *
 * A textarea with a compact formatting toolbar (bold / italic / underline / heading /
 * bullet / numbered / link / indent / outdent) that inserts or wraps markdown at the
 * selection, plus an Edit ⇄ Preview toggle that renders the markdown via <Markdown>. Not
 * a heavyweight WYSIWYG — it keeps markdown as the source of truth (so it round-trips
 * cleanly with the data and the read-only renderer) while giving non-markdown users
 * buttons + a live preview. Tab/Shift+Tab inside the textarea indent/outdent the current
 * line(s) instead of moving focus — this is how a bullet or numbered line gets nested
 * (2 spaces per level, matching <Markdown>'s and the PDF renderer's own convention).
 *
 * Props: value, onChange(string), placeholder, minHeight
 */

import { useRef, useState } from 'react'
import Markdown from './Markdown.jsx'

const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
  color: '#07272D', background: '#f4f7f8', border: '0.5px solid rgba(7,39,45,0.18)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none', lineHeight: 1.5, resize: 'vertical',
}
const btn = {
  background: '#fff', border: '0.5px solid rgba(7,39,45,0.18)', borderRadius: 4,
  cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#07272D',
  padding: '2px 7px', lineHeight: 1.4,
}

export default function MarkdownField({ value, onChange, placeholder, minHeight = 90 }) {
  const ref = useRef(null)
  const [preview, setPreview] = useState(false)

  // Wrap the current selection with before/after; if nothing selected, insert a stub.
  function wrap(before, after = before, stub = 'text') {
    const el = ref.current
    if (!el) return
    const s = el.selectionStart, e = el.selectionEnd
    const sel = value.slice(s, e) || stub
    const next = value.slice(0, s) + before + sel + after + value.slice(e)
    onChange(next)
    requestAnimationFrame(() => {
      el.focus()
      el.selectionStart = s + before.length
      el.selectionEnd = s + before.length + sel.length
    })
  }
  // Prefix the start of each selected line (lists / headings).
  function prefixLines(prefix) {
    const el = ref.current
    if (!el) return
    const s = el.selectionStart, e = el.selectionEnd
    const lineStart = value.lastIndexOf('\n', s - 1) + 1
    const block = value.slice(lineStart, e)
    const numbered = prefix === '1. '
    const replaced = block.split('\n').map((ln, i) => (numbered ? (i + 1) + '. ' : prefix) + ln).join('\n')
    const next = value.slice(0, lineStart) + replaced + value.slice(e)
    onChange(next)
    requestAnimationFrame(() => { el.focus() })
  }
  function addLink() {
    const el = ref.current
    if (!el) return
    const s = el.selectionStart, e = el.selectionEnd
    const sel = value.slice(s, e) || 'label'
    const next = value.slice(0, s) + '[' + sel + '](https://)' + value.slice(e)
    onChange(next)
    requestAnimationFrame(() => { el.focus() })
  }
  // Add/remove one 2-space nesting level on every selected line's start — same slicing shape as
  // prefixLines (from selection start's line-start through selection end; a line only partially
  // selected still gets its start touched, its own trailing text is untouched).
  function indentLines(add) {
    const el = ref.current
    if (!el) return
    const s = el.selectionStart, e = el.selectionEnd
    const lineStart = value.lastIndexOf('\n', s - 1) + 1
    const block = value.slice(lineStart, e)
    const replaced = block.split('\n').map(ln => (add ? '  ' + ln : ln.replace(/^ {1,2}/, ''))).join('\n')
    const next = value.slice(0, lineStart) + replaced + value.slice(e)
    onChange(next)
    requestAnimationFrame(() => { el.focus() })
  }
  function onKeyDown(e) {
    if (e.key === 'Tab') { e.preventDefault(); indentLines(!e.shiftKey) }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4, flexWrap: 'wrap' }}>
        <button type="button" style={{ ...btn, fontWeight: 700 }} title="Bold" onClick={() => wrap('**')}>B</button>
        <button type="button" style={{ ...btn, fontStyle: 'italic' }} title="Italic" onClick={() => wrap('_')}>I</button>
        <button type="button" style={{ ...btn, textDecoration: 'underline' }} title="Underline" onClick={() => wrap('++')}>U</button>
        <button type="button" style={btn} title="Heading" onClick={() => prefixLines('## ')}>H</button>
        <button type="button" style={btn} title="Bullet list" onClick={() => prefixLines('- ')}>•</button>
        <button type="button" style={btn} title="Numbered list" onClick={() => prefixLines('1. ')}>1.</button>
        <button type="button" style={btn} title="Link" onClick={addLink}>↗</button>
        <span style={{ width: 1, alignSelf: 'stretch', background: 'rgba(7,39,45,0.14)' }} />
        <button type="button" style={btn} title="Outdent (or Shift+Tab)" onClick={() => indentLines(false)}>⇤</button>
        <button type="button" style={btn} title="Indent — nests a bullet/numbered line (or Tab)" onClick={() => indentLines(true)}>⇥</button>
        <span style={{ flex: 1 }} />
        <button type="button" style={{ ...btn, background: preview ? '#07272D' : '#fff', color: preview ? '#fff' : '#07272D' }}
          onClick={() => setPreview(p => !p)}>{preview ? 'Edit' : 'Preview'}</button>
      </div>
      {preview ? (
        <div style={{ ...inputStyle, background: '#fff', minHeight, overflowY: 'auto' }}>
          {value ? <Markdown text={value} /> : <span style={{ color: '#9bb0b6' }}>Nothing to preview</span>}
        </div>
      ) : (
        <textarea
          ref={ref}
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          style={{ ...inputStyle, minHeight }}
        />
      )}
    </div>
  )
}
